import { CopilotActions } from 'actions/actionTypes';

import { IS_COPILOT_OMNISEARCH_PAGINATION_ENABLED } from 'helpers/copilotOmnisearchPagination';

function newOmnisearchRequestId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `omni-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Stable key for {@link state.copilot.contextOmnisearchCache}. Matches RECEIVE and hydrate lookups.
 * @param {string} [query]
 * @param {string | null} [searchType] `null` / omit encodes global @-mention (no `search_type`).
 * @param {string | null} [filterSignature] See {@link buildOmnisearchFilterSignature} — folded in so a
 * cached pre-filter response is never served once a "Select all" filter changes for the same query/type.
 * @returns {string}
 */
export function buildOmnisearchCacheKey(query, searchType, filterSignature) {
  const q = String(query ?? '').trim();
  const st =
    searchType != null && String(searchType).trim() !== ''
      ? String(searchType).trim()
      : null;
  const fs =
    filterSignature != null && String(filterSignature).trim() !== ''
      ? String(filterSignature).trim()
      : '';
  return `${st ?? 'global'}::${q}::${fs}`;
}

/**
 * Stable signature for the active "Select all" filter-groups state, so equal filter state produces
 * an equal cache key regardless of object key insertion order (`advancedContextFilterGroupsByCategory`
 * is rebuilt fresh on every render).
 * @param {Record<string, unknown> | null | undefined} filterGroupsByCategory
 * @returns {string} Empty string when no category has an active filter.
 */
export function buildOmnisearchFilterSignature(filterGroupsByCategory) {
  if (!filterGroupsByCategory || typeof filterGroupsByCategory !== 'object') {
    return '';
  }
  const keys = Object.keys(filterGroupsByCategory)
    .filter(key => {
      const entry = filterGroupsByCategory[key];
      return (
        Array.isArray(entry?.filterGroups) &&
        entry.filterGroups.some(
          group => Array.isArray(group?.rules) && group.rules.length > 0
        )
      );
    })
    .sort();
  if (keys.length === 0) {
    return '';
  }
  return JSON.stringify(
    keys.map(key => {
      const entry = filterGroupsByCategory[key] || {};
      return [
        key,
        {
          filterGroups: (entry.filterGroups || []).map(group => ({
            group_operator: group.group_operator ?? group.groupOperator,
            rules: (group.rules || []).map(rule => ({
              rule_uid: rule.rule_uid ?? rule.ruleUid,
              operator: rule.operator,
              values: rule.values,
              details: rule.details,
              field_type: rule.field_type ?? rule.fieldType,
              rule_object_type: rule.rule_object_type ?? rule.ruleObjectType,
              rule_operator_in_group:
                rule.rule_operator_in_group ?? rule.ruleOperatorInGroup
            }))
          })),
          excludedUuids: [...(entry.excludedUuids || [])].sort()
        }
      ];
    })
  );
}

/**
 * Omnisearch WS payloads may echo `request_id` (snake_case) while the client tracks `requestId`.
 * Without this, {@link CopilotActions.RECEIVE_COPILOT_OMNISEARCH} cannot match the active slot and
 * stale responses are not dropped.
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {string | null}
 */
export function readCopilotOmnisearchRequestIdFromWsData(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const id = data.requestId ?? data.request_id;
  if (id == null) {
    return null;
  }
  return String(id);
}

/**
 * @-mention omnisearch: WebSocket `message_type: omnisearch` (see `helpers/websockets.js` send).
 * Optional `request_id` in the payload/response is used to drop stale responses.
 *
 * When a matching entry exists in `contextOmnisearchCache`, dispatches
 * `HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE` instead (no socket, no loading) — e.g. Add context tab switch.
 *
 * @param {string} query
 * @param {{
 *   searchType?: string,
 *   bypassCache?: boolean,
 *   page?: number,
 *   pageSize?: number,
 *   filterGroupsByCategory?: Record<string, { filterGroups: unknown[], excludedUuids: string[], filterSummary: string }>
 * }} [options] Optional `searchType` scopes Add context tab omnisearch; omit for global @-mention.
 * `filterGroupsByCategory` mirrors any active "Select all" advanced-context filters (per
 * `meetings`/`deals`/`accounts`) so the live search is scoped by them too — see
 * `buildAdvancedContextFilterGroupsForOmnisearch` in `CopilotTextArea/index.tsx`.
 */
export const sendCopilotOmnisearchQuery =
  (query, options = {}) =>
  (dispatch, getState) => {
    const {
      searchType,
      page,
      pageSize,
      bypassCache = false,
      filterGroupsByCategory
    } = options;
    const nextQ = String(query ?? '').trim();
    const nextSt =
      searchType != null && String(searchType).trim() !== ''
        ? String(searchType).trim()
        : null;
    const filterSignature = buildOmnisearchFilterSignature(
      filterGroupsByCategory
    );
    const cacheKey = buildOmnisearchCacheKey(query, nextSt, filterSignature);
    if (!bypassCache && typeof getState === 'function') {
      const cached = getState().copilot?.contextOmnisearchCache?.[cacheKey];
      if (cached?.results != null) {
        dispatch({
          type: CopilotActions.HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE,
          requestQuery: nextQ,
          requestSearchType: nextSt,
          requestFilterSignature: filterSignature,
          results: cached.results
        });
        return;
      }
    }
    const requestId = newOmnisearchRequestId();
    // The backend rejects `page` without `search_type`, so a global @-mention search stays unpaged.
    const nextPage =
      IS_COPILOT_OMNISEARCH_PAGINATION_ENABLED && nextSt != null && page != null
        ? page
        : undefined;
    dispatch({
      type: CopilotActions.SEND_COPILOT_OMNISEARCH,
      query: nextQ,
      requestId,
      searchType: nextSt,
      page: nextPage,
      pageSize: IS_COPILOT_OMNISEARCH_PAGINATION_ENABLED ? pageSize : undefined,
      filterGroupsByCategory,
      filterSignature
    });
  };

/**
 * @param {object} [options]
 * @param {boolean} [options.preserveDefaultOmnisearch] when true, keep `requestQuery` and `results` so
 * default (empty query) WebSocket omnisearch can be reused on the next open without a flash of empty.
 */
export const clearCopilotOmnisearch = (options = {}) => ({
  type: CopilotActions.CLEAR_COPILOT_OMNISEARCH,
  preserveDefaultOmnisearch: Boolean(options.preserveDefaultOmnisearch)
});

/** Fixed page size for omnisearch "Load more" requests (backend requires `search_type` whenever `page` is sent). */
export const OMNISEARCH_LOAD_MORE_PAGE_SIZE = 10;

/**
 * Stable key for {@link state.copilot.contextOmnisearchLoadMore} buckets. Always scoped to one
 * `searchType` (backend requires it whenever `page` is sent), so this is the same shape as
 * {@link buildOmnisearchCacheKey} but with a non-optional, non-null `searchType`.
 * @param {string} searchType
 * @param {string} [query]
 * @param {string | null} [filterSignature]
 * @returns {string}
 */
export function buildOmnisearchLoadMoreKey(searchType, query, filterSignature) {
  return buildOmnisearchCacheKey(query, searchType, filterSignature);
}

/**
 * "Load more" for a single omnisearch category page. Unlike {@link sendCopilotOmnisearchQuery},
 * this never reads/writes `contextOmnisearchCache` or the single-slot `contextOmnisearch` request —
 * see `contextOmnisearchLoadMore` in `reducers/copilot.js` for the per-category bucket this feeds.
 * @param {string} query
 * @param {string} searchType Required — backend requires `search_type` whenever `page` is sent.
 * @param {number} page
 * @param {{
 *   pageSize?: number,
 *   filterGroupsByCategory?: Record<string, { filterGroups: unknown[], excludedUuids: string[], filterSummary: string }>
 * }} [options]
 */
export const sendCopilotOmnisearchLoadMoreQuery =
  (query, searchType, page, options = {}) =>
  dispatch => {
    if (!IS_COPILOT_OMNISEARCH_PAGINATION_ENABLED) {
      return;
    }
    const nextSt = String(searchType ?? '').trim();
    if (!nextSt) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
          'sendCopilotOmnisearchLoadMoreQuery: searchType is required (backend requires search_type whenever page is set)'
        );
      }
      return;
    }
    const {
      pageSize = OMNISEARCH_LOAD_MORE_PAGE_SIZE,
      filterGroupsByCategory
    } = options;
    const filterSignature = buildOmnisearchFilterSignature(
      filterGroupsByCategory
    );
    const requestId = newOmnisearchRequestId();
    dispatch({
      type: CopilotActions.SEND_COPILOT_OMNISEARCH_PAGE,
      query: String(query ?? '').trim(),
      requestId,
      searchType: nextSt,
      page,
      pageSize,
      filterGroupsByCategory,
      filterSignature
    });
  };

/** Clears all `contextOmnisearchLoadMore` buckets (e.g. mention query changed, popover closed). */
export const clearCopilotOmnisearchLoadMore = () => ({
  type: CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE
});
